/**
 * Özel İzin ver / geri al penceresi (Kişi Detay › Kontroller › Özel İzin kartı). #475 (backend #474):
 *  · kimliği doğrulanmamış kişiye izin verilmez: "İzni Ver" kapalı, "Geri Al" açık (geri almak her zaman serbest) ve pencerede
 *    "Önce kimliği doğrulayın" + Kimlik penceresini açan düğme çıkar;
 *  · yalnızca üç neden seçilebilir (Saka Grup inisiyatifi / Organizatör talebi üzerine / Belgeleri alınmıştır); eski bir izin
 *    (tip 1 / 4) düzenlenirse başlangıç tipi 3 olur;
 *  · sayfa bayat kalmışsa backend 409 `KIMLIK_DOGRULANMAMIS` döner: mesaj gösterilir ve kişi yeniden okunur.
 * Önceden PersonelDetayPage.jsx'in içindeydi.
 */
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";
import { AlertTriangle, IdCard } from "lucide-react";
import {
  grantSinglePermit, revokeSinglePermit,
  OZEL_IZIN_VERILEBILIR_TIPLER, OZEL_IZIN_VARSAYILAN_TIP, isKimlikDogrulanmamisHatasi,
} from "../../services/kisiAdminService";

// #475 (backend #474): kimliği doğrulanmamış kişiye izin verilmez (İzni Ver kapalı, Geri Al açık); yalnızca 3 / 2 / 5 seçilebilir.
// Eski bir izin (tip 1 / 4) düzenlenirse başlangıç tipi 3'tür ve Açıklama ön dolu gelmez (eski satırın metni, backend'in sabit cümlesidir).
export default function OzelIzinModal({ personId, fuarId, mevcut, kimlikTamam, onKimlikDogrula, onClose, onSaved, onKimlikDegisti }) {
  const { t: tx } = useTranslation("moduller");
  const mevcutTip = mevcut?.typeId ?? mevcut?.permitTypeId;
  const mevcutVerilebilir = OZEL_IZIN_VERILEBILIR_TIPLER.some((t) => t.value === mevcutTip);
  // WAS (until #475): useState(String(mevcut?.typeId ?? mevcut?.permitTypeId ?? 3)) and useState(mevcut?.typeString ?? mevcut?.permitTypeString ?? "")
  const [typeId, setTypeId] = useState(String(mevcutVerilebilir ? mevcutTip : OZEL_IZIN_VARSAYILAN_TIP));
  const [aciklama, setAciklama] = useState(mevcutVerilebilir ? (mevcut?.typeString ?? mevcut?.permitTypeString ?? "") : "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function grant() {
    setSaving(true);
    setError(null);
    try {
      await grantSinglePermit(personId, { fuarId, permitTypeId: Number(typeId), aciklama: aciklama.trim() });
      onSaved(tx("personelDetay.ozelIzinVerildiFuar", { fuarId }));
    } catch (e) {
      setError(e.message);
      // Sayfa bayat kalmış olabilir (kimlik arada sıfırlandı / reddedildi): kişiyi yeniden oku, uyarı ve düğme kendiliğinden gelir.
      if (isKimlikDogrulanmamisHatasi(e)) onKimlikDegisti?.();
    } finally {
      setSaving(false);
    }
  }

  async function revoke() {
    setSaving(true);
    setError(null);
    try {
      await revokeSinglePermit(personId, fuarId);
      onSaved(tx("personelDetay.ozelIzinGeriAlindiFuar", { fuarId }));
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal isOpen toggle={onClose}>
      <ModalHeader toggle={onClose}>{tx("personelDetay.ozelIzin")}</ModalHeader>
      <ModalBody>
        {error && <Alert fade={false} color="danger">{error}</Alert>}
        {!kimlikTamam && (
          <Alert fade={false} color="warning" className="small py-2 d-flex align-items-center gap-2 flex-wrap">
            <IdCard size={16} className="flex-shrink-0" />
            <span>{tx("personelDetay.onceKimligiDogrulayin")}</span>
            <Button color="warning" size="sm" outline className="ms-auto" disabled={saving} onClick={onKimlikDogrula}>
              {tx("personelDetay.kimligiDogrula")}
            </Button>
          </Alert>
        )}
        <Alert fade={false} color="warning" className="small d-flex gap-2">
          <AlertTriangle size={16} className="flex-shrink-0" />
          <span>
            {tx("personelDetay.calisan")}{" "}<strong>{tx("personelDetay.kvkkVeKimlikDisindaHicbirBelgeOlmadan")}</strong>{" "}{tx("personelDetay.iceriGirerIzniVerenKayda")}
          </span>
        </Alert>

        <Label className="small">{tx("personelDetay.izinTipi")}</Label>
        {/* WAS (until #475): OZEL_IZIN_TIPLERI.map(...) — dört tipin hepsi seçilebiliyordu */}
        <Input type="select" value={typeId} disabled={!kimlikTamam} onChange={(e) => setTypeId(e.target.value)}>
          {OZEL_IZIN_VERILEBILIR_TIPLER.map((t) => <option key={t.value} value={String(t.value)}>{t.label}</option>)}
        </Input>
        <div className="form-text small">{tx("personelDetay.tipYalnizcaKiminInisiyatifiOldugunu")}</div>

        <Label className="small mt-3">{tx("personelDetay.aciklama")}</Label>
        <Input
          type="textarea" rows={2} value={aciklama} disabled={!kimlikTamam} onChange={(e) => setAciklama(e.target.value)}
          placeholder={tx("personelDetay.ozelIzinVerilmistir")}
        />

        <div className="small text-muted mt-3">
          {tx("personelDetay.izin")}{" "}<strong>{tx("personelDetay.fuar")}{" "}{fuarId}</strong>{" "}{tx("personelDetay.icinVerilirBaskaFuarlariEtkilemez")}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose} disabled={saving}>{tx("personelDetay.vazgec")}</Button>
        {mevcut && (
          <Button color="danger" outline onClick={revoke} disabled={saving}>{tx("personelDetay.geriAl")}</Button>
        )}
        <Button color="warning" onClick={grant} disabled={saving || !kimlikTamam}>
          {saving ? <Spinner size="sm" /> : tx("personelDetay.izniVer")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
